/**
 * Wizard (beslisboom) frontend.
 *
 * Ported from the Den Haag plugin. Requires jQuery and lodash (WordPress bundles
 * both; they are declared as dependencies of the client script in functions.php).
 * The markup template lives in templates/wizard-step-template.php and is printed
 * in the footer by the HLTsamen\Theme\Wizard component.
 */
export default class Wizard {
  wizard_data = null;
  wizard_template = null;
  trail = null;
  choices = null;
  answers = null;
  wizard_id = null;
  intro = null;
  button = '';
  debug = false;
  debug_i18n = null;
  $ = jQuery;
  _ = lodash;

  /**
   * Constructor.
   * @param {object} _wizard_data Object containing wizard data.
   */
  constructor(_wizard_data) {
    const self = this;
    this.wizard_data = _wizard_data.slides;
    this.wizard_template = self._.template( self.$( 'script#wizard-tpl' ).html() );
    this.trail = [];
    this.wizard_id = _wizard_data.ID;
    this.intro = _wizard_data.intro;
    // Start-button text; when set, the first slide is only built after the visitor clicks the button.
    this.button = _wizard_data.button || '';
    this.debug = !!_wizard_data.debug;
    this.debug_i18n = _wizard_data.debug_i18n || {};

    this.get_browser_and_device_info();
    this.open_by_id( this.wizard_id );
  }

  /**
   * Retrieve data for displaying a slide.
   * @param {int|string} step The step-number to get the slide data for.
   */
  get_slide(step) {
    return this.wizard_data[step];
  };

  /**
   * The sessionStorage key under which this wizard's progress is kept.
   * @return {string}
   */
  storage_key() {
    return 'hltsamen-wizard-' + this.wizard_id;
  };

  /**
   * Persist the current trail and choices to sessionStorage so that going
   * back a step - or reloading the page - restores earlier answers.
   */
  save_state() {
    try {
      window.sessionStorage.setItem(
        this.storage_key(),
        JSON.stringify( { trail: this.trail, choices: this.choices, answers: this.answers } )
      );
    } catch ( err ) {
      // sessionStorage unavailable (e.g. private mode) or full; the wizard
      // keeps working in-memory, we just can't remember across reloads.
    }
  };

  /**
   * Restore trail and choices from sessionStorage. Falls back to a fresh
   * start when nothing is stored, storage is unavailable, or the stored
   * trail no longer matches the current slides.
   */
  load_state() {
    const self = this;
    let restored = false;
    try {
      const raw = window.sessionStorage.getItem( self.storage_key() );
      if ( raw ) {
        const state = JSON.parse( raw );
        if ( state
          && Array.isArray( state.trail )
          && state.trail.length
          && self.trail_is_valid( state.trail ) ) {
          self.trail = state.trail;
          self.choices = ( state.choices && 'object' === typeof state.choices ) ? state.choices : {};
          self.answers = ( state.answers && 'object' === typeof state.answers ) ? state.answers : {};
          restored = true;
        }
      }
    } catch ( err ) {
      // Unavailable storage or corrupt JSON; start fresh.
    }
    if ( ! restored ) {
      // Slides are keyed 1-based (see the Wizard PHP component); the first slide is "1".
      self.trail = ["1"];
      self.choices = {};
      self.answers = {};
    }
  };

  /**
   * Guard against a stored trail that points at slides that no longer exist
   * (e.g. the wizard was edited since the visitor's last session).
   * @param {Array} trail The trail to validate.
   * @return {boolean}
   */
  trail_is_valid(trail) {
    return trail.every( ( step ) => 'undefined' !== typeof this.get_slide( step ) );
  };

  /**
   * Re-check the previously chosen radio for the current step, if any, and
   * let the existing change-handler reveal the matching Continue button/link.
   */
  restore_choice() {
    const self = this;
    const current = self.trail[self.trail.length - 1];
    if ( ! ( current in self.choices ) ) {
      return;
    }
    const $inputs = self.$( 'div#wizard-' + self.wizard_id ).find( 'input[name="wizard-choice"]' );
    // Prefer matching by button index (exact answer); two answers can share a target slide.
    const answer = self.answers && self.answers[current];
    let $input = self.$();
    if ( answer && answer.index ) {
      $input = $inputs.filter( '[data-index="' + answer.index + '"]' );
    }
    if ( ! $input.length ) {
      const stored = String( self.choices[current] );
      $input = $inputs.filter( function () {
        return String( this.value ) === stored;
      } );
    }
    if ( $input.length ) {
      $input.prop( 'checked', true ).trigger( 'change' );
    }
  };

  /**
   * Track virtual page-view.
   * @param {string} url The Virtual URL to send to GA.
   */
  track_view(url) {
    if ('function' === typeof gtag) {
      gtag( 'event', 'page_view', {
        'page_title': "Wizard",
        'page_path': url,
        'transport': 'beacon',
        'anonymize_ip': true,
        'force_ssl': true,
        'non_interaction': false,
      } );
    }

    // fire custom event to the DOM to let other scripts know the content has changed.
    const wizardChangeEvent = new CustomEvent( 'hltsamen.wizard.change', { detail: { wizardId: this.wizard_id } } );
    document.dispatchEvent( wizardChangeEvent );
  };

  /**
   * Forget everything: remove this wizard's sessionStorage entry and reset the
   * in-memory state to a fresh start at the first slide.
   */
  reset_session_storage() {
    try {
      window.sessionStorage.removeItem( this.storage_key() );
    } catch ( err ) {
      // sessionStorage unavailable; in-memory reset below still applies.
    }
    this.trail = ["1"];
    this.choices = {};
    this.answers = {};
  };

  /**
   * Render the debug decorations: score per answer, and a debug box with the
   * evaluated total score, the trail, and a reset link, under the wizard.
   */
  render_debug() {
    const self = this;
    const $container = self.$( 'div#wizard-' + self.wizard_id );

    // Show each answer's score next to its label.
    $container.find( 'input[name="wizard-choice"]' ).each( function () {
      const score = parseFloat( self.$( this ).attr( 'data-score' ) ) || 0;
      $container.find( 'label[for="' + this.id + '"]' ).append( ' <span class="wizard__debug-score">(+' + score + ')</span>' );
    } );

    const $box = self.$( '<div class="wizard__debug"></div>' );
    $box.append( self.$( '<strong></strong>' ).text( self.debug_i18n.title || 'Wizard debug' ) );
    $box.append( self.$( '<div></div>' ).text( ( self.debug_i18n.total || 'Total score:' ) + ' ' + self.compute_score() ) );
    $box.append( self.$( '<div></div>' ).text( ( self.debug_i18n.trail || 'Trail:' ) + ' ' + self.trail.join( ' → ' ) ) );
    $box.append(
      self.$( '<a href="#" class="action-reset-debug"></a>' )
        .text( self.debug_i18n.reset || 'Reset sessions storage and restart wizard' )
    );
    $container.append( $box );
  };

  /**
   * Total score of the answers given on the current path.
   *
   * Only slides on the current trail count (abandoned branches don't), each
   * slide counts at most once (its latest answer), and the slide currently
   * being shown is excluded - the visitor is (re)answering it right now.
   *
   * @return {number}
   */
  compute_score() {
    const counted = {};
    for (let i = 0; i < this.trail.length - 1; i++) {
      counted[this.trail[i]] = true;
    }
    let total = 0;
    for (const slide in counted) {
      if (this.answers && this.answers[slide]) {
        total += this.answers[slide].score || 0;
      }
    }
    return total;
  };

  /**
   * The score sections of a slide that match the current total score.
   *
   * A section with both bounds empty is a fallback: it is only shown when no
   * other section matches. Bounds are inclusive; an empty bound is open-ended.
   *
   * @param {object} slide The slide data.
   * @return {Array}
   */
  matching_sections(slide) {
    const sections = slide.score_sections;
    if (!sections || !sections.length) {
      return [];
    }
    const total = this.compute_score();
    const bound = (value) => ('' === value || null === value || 'undefined' === typeof value) ? null : parseFloat( value );
    const matches = [];
    const fallbacks = [];
    sections.forEach( (section) => {
      const from = bound( section.s_from );
      const to = bound( section.s_to );
      if (null === from && null === to) {
        fallbacks.push( section );
        return;
      }
      if ((null === from || total >= from) && (null === to || total <= to)) {
        matches.push( section );
      }
    } );
    return matches.length ? matches : fallbacks;
  };

  /**
   * Build the current slide.
   */
  build() {
    const self = this;
    if (self.trail.length === 0) {
      // nothing to build
      console.error( 'Wizard cowardly refuses to build tower with 0 layers' );
      return;
    }
    let data = self.get_slide( self.trail[self.trail.length - 1] ); // current slide, at trail end
    data.intro = self.intro;
    data.prev = false;
    if (self.trail.length > 1) {
      data.prev = self.trail[self.trail.length - 2]; // we want the before-last element
    }
    data.trail = self.trail;
    // Score is evaluated just before showing the slide; going back and changing
    // an answer is therefore always reflected in the total.
    data.total_score = self.compute_score();
    data.matched_sections = self.matching_sections( data );

    self.$( 'div#wizard-' + self.wizard_id ).empty().html( self.wizard_template( data ) );
    self.$( 'div#wizard-' + self.wizard_id ).find( 'form' ).on( 'submit', function (e) {
      e.preventDefault();
      return false;
    } );
    if (self.debug) {
      self.render_debug();
    }
    self.restore_choice();
    self.track_view( data.url );
    this.hasBuilt = true;
  };

  /**
   * Click handler to go to a different slide, or maybe open a page.
   * @param {event} e The click-event.
   * @param {Wizard} self The current object.
   */
  click_handler(e, self) {
    // click handler is bound on the container, 'this' is not the correct element to check
    let $el = self.$( e.target );

    if ($el.is( '.action-start' )) {
      e.preventDefault();
      self.build();
      self.focus_title_on_change();
      return;
    }
    if ($el.is( '.action-back' )) {
      e.preventDefault();
      self.trail.pop();
      self.save_state();
      self.build();
      self.focus_title_on_change();
      return;
    }
    if ($el.is( '.action-home' )) {
      e.preventDefault();
      if ($el.attr( 'data-reset-session' )) {
        // "Reset session storage" answers forget everything instead of only restarting.
        self.reset_session_storage();
      } else {
        self.trail = [self.trail.shift()];
        self.save_state();
      }
      self.build();
      self.focus_title_on_change();
      return;
    }
    if ($el.is( '.action-reset-debug' )) {
      e.preventDefault();
      self.reset_session_storage();
      self.build();
      self.focus_title_on_change();
      return;
    }
    if ($el.is( '.action-next' )) {
      let $form = $el.closest( 'form' );
      let $checked = $form.find( 'input:checked' );
      let choice = $checked.val();
      let current = self.trail[self.trail.length - 1];
      self.choices[current] = choice;
      // Remember the answer itself (not just its target); this is the basis for score calculation.
      self.answers[current] = {
        index: $checked.attr( 'data-index' ) || '',
        score: parseFloat( $checked.attr( 'data-score' ) ) || 0,
        choice: choice,
      };
      self.trail.push( choice );
      self.save_state();
      self.build();
      self.focus_title_on_change();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  /**
   * Change handler to go to a different slide, or maybe open a page.
   * @param {event} e The change-event.
   * @param {Wizard} self The current object.
   */
  change_handler(e, self) {
    // change handler is bound on the container, 'this' is not the correct element to check
    let $el = self.$( e.target );
    let $wizard = $el.closest( '.wizard' );

    let target = $el.attr( 'data-target-id' ) || false;
    let href = $el.attr( 'href' ) || false;
    let targetAttr = $el.attr( 'target' ) || '';

    // Handle footer button/link visibility
    let $footerButton = $wizard.find( 'button.action-next' );
    let $footerLink = $wizard.find( 'a.action-next-link' );

    // Reset both to hidden state first
    $footerButton.addClass( 'hidden-until-choice-made' );
    $footerLink.addClass( 'hidden-until-choice-made' );

    // If the radio has an href (old version remnant), show link and hide button
    if (href && href !== '#') {
      // Update link with href and target
      $footerLink.attr( 'href', href );
      if (targetAttr === '_blank') {
        $footerLink.attr( 'target', '_blank' );
        $footerLink.attr( 'rel', 'noopener noreferrer' );
      } else {
        $footerLink.removeAttr( 'target' ).removeAttr( 'rel' );
      }

      // Show link
      $footerLink.removeClass( 'hidden-until-choice-made' );
    } else if (target) {
      // Show button for wizard navigation
      $footerButton.removeClass( 'hidden-until-choice-made' );
    }
    // fire custom event to the DOM to let other scripts know the content has changed.
    const wizardChangeEvent = new CustomEvent( 'hltsamen.wizard.change', { detail: { wizardId: self.wizard_id } } );
    document.dispatchEvent( wizardChangeEvent );
  };

  /**
   * Focuses the first legend or strong in the current visible wizard step, if present.
   */
  focus_title_on_change() {
    const wizardContainer = document.querySelector( 'div#wizard-' + this.wizard_id );
    if (!wizardContainer) return;

    let focusTarget = wizardContainer.querySelector( 'legend' );
    if (!focusTarget) {
      focusTarget = wizardContainer.querySelector( 'strong' );
    }

    if (focusTarget) {
      if (typeof focusTarget.tabIndex !== 'number' || focusTarget.tabIndex < 0) {
        focusTarget.tabIndex = -1;
      }
      focusTarget.focus();
    }
  }

  get_browser_and_device_info() {
    const ua = navigator.userAgent;
    const isMobile = /Mobi|Android/i.test( ua );
    const isTablet = /Tablet|iPad/i.test( ua );

    let browser = "Unknown";
    if (/Chrome\/\d+/.test( ua ) && !/Edge\/|OPR\//.test( ua )) {
      browser = "chrome";
    } else if (/Safari\/\d+/.test( ua ) && !/Chrome/.test( ua )) {
      browser = "safari";
    } else if (/Firefox\/\d+/.test( ua )) {
      browser = "firefox";
    } else if (/Edg\//.test( ua )) {
      browser = "edge";
    } else if (/OPR\//.test( ua )) {
      browser = "opera";
    } else if (/MSIE |Trident\//.test( ua )) {
      browser = "internet-explorer";
    }

    const deviceType = isTablet ? "tablet" : isMobile ? "mobile" : "desktop";

    browser && document.body.classList.add( `is-${browser}` );
    deviceType && document.body.classList.add( `is-${deviceType}` );
  }

  /**
   * Show the wizard.
   */
  show() {
    const self = this;
    self.$( 'div#wizard-' + self.wizard_id ).show().removeClass( 'hidden' ).on( 'click', function (e) {
      return self.click_handler( e, self );
    } ).on( 'change', 'input', function (e) {
      return self.change_handler( e, self );
    } );
  };

  /**
   * Show the start button instead of the first slide.
   *
   * The button is normally rendered server-side (see the Wizard PHP component)
   * so it is visible before this script runs; it is only added here when the
   * container does not have one, e.g. custom markup.
   */
  show_start_button() {
    const self = this;
    const $container = self.$( 'div#wizard-' + self.wizard_id );
    if ($container.find( '.action-start' ).length) {
      return;
    }
    $container.empty().append(
      self.$( '<button type="button" class="wizard__start action-start button button--primary"></button>' ).text( self.button )
    );
  };

  /**
   * Bootstrap wizard by ID.
   * @param {int|string} wizard The wizard ID.
   */
  open_by_id(wizard) {
    this.wizard_id = wizard;
    this.load_state(); // restore trail + choices from sessionStorage, or start fresh
    this.show();       // bind handlers first so restore_choice()'s change-trigger fires
    if (this.button) {
      // Wait for the visitor; the click handler builds the first slide.
      this.show_start_button();
      return;
    }
    this.build();
  };
}
